"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "../../lib/utils";
import {
	SIDEBAR_SECTIONS_COOKIE_MAX_AGE,
	SIDEBAR_SECTIONS_COOKIE_NAME,
	serializeClosedSectionIds,
} from "../../lib/sidebar-sections";
import { textVariants } from "./text";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "./collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { TooltipContent, TooltipRoot, TooltipTrigger } from "./tooltip";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter as SidebarFooterPrimitive,
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	useSidebar,
} from "./sidebar";

const TOOLTIP_INTENT_DELAY_MS = 80;
const FLYOUT_OPEN_DELAY_MS = 50;
const FLYOUT_LEAVE_GRACE_MS = 100;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AppSidebarSectionLink = {
	id: string;
	label: React.ReactNode;
	href?: string;
	isActive?: boolean;
	asChild?: boolean;
	children?: React.ReactNode;
};

type AppSidebarSectionProps = {
	label: string;
	icon: React.ReactNode;
	links: AppSidebarSectionLink[];
	/**
	 * Stable key for the persisted open/closed state. Defaults to `label`, so a
	 * renamed label resets that section to open. Only read under an
	 * `AppSidebarSectionStateProvider`.
	 */
	sectionId?: string;
	/**
	 * Initial state when nothing is persisted. Under an
	 * `AppSidebarSectionStateProvider` the persisted state wins, and sections
	 * default to open.
	 */
	defaultOpen?: boolean;
	extraContent?: React.ReactNode;
};

type AppSidebarItemProps = {
	label: string;
	isActive?: boolean;
	href?: string;
	asChild?: boolean;
	children?: React.ReactNode;
};

type AppSidebarHeaderProps = {
	logo?: React.ReactNode;
	className?: string;
};

type AppSidebarProps = Omit<
	React.ComponentProps<typeof Sidebar>,
	"collapsible"
> & {
	header?: React.ReactNode;
	footer?: React.ReactNode;
	collapsible?: "offcanvas" | "icon" | "none";
};

// ---------------------------------------------------------------------------
// Section open/closed state
// ---------------------------------------------------------------------------

type AppSidebarSectionStateContextValue = {
	isSectionOpen: (sectionId: string) => boolean;
	setSectionOpen: (sectionId: string, open: boolean) => void;
};

const AppSidebarSectionStateContext =
	React.createContext<AppSidebarSectionStateContextValue | null>(null);

type AppSidebarSectionStateProviderProps = {
	/**
	 * Sections the user has closed, read from the cookie on the server so the
	 * first paint already matches — see `lib/sidebar-sections`.
	 */
	defaultClosedSectionIds?: string[];
	/** Cookie to persist into. Pass `null` to keep the state in memory only. */
	cookieName?: string | null;
	children: React.ReactNode;
};

/**
 * Remembers which sections the user closed, and writes them to a cookie the
 * same way the sidebar's own collapse toggle does. Sections opt in by rendering
 * inside it; without it each section keeps its own in-memory state.
 */
function AppSidebarSectionStateProvider({
	defaultClosedSectionIds = [],
	cookieName = SIDEBAR_SECTIONS_COOKIE_NAME,
	children,
}: AppSidebarSectionStateProviderProps) {
	const [closedSectionIds, setClosedSectionIds] = React.useState(
		defaultClosedSectionIds,
	);

	const setSectionOpen = React.useCallback(
		(sectionId: string, open: boolean) => {
			const isClosed = closedSectionIds.includes(sectionId);
			if (open === !isClosed) {
				return;
			}

			const next = open
				? closedSectionIds.filter((id) => id !== sectionId)
				: [...closedSectionIds, sectionId];

			setClosedSectionIds(next);

			if (cookieName) {
				document.cookie = `${encodeURIComponent(cookieName)}=${serializeClosedSectionIds(next)}; path=/; max-age=${SIDEBAR_SECTIONS_COOKIE_MAX_AGE}; SameSite=Lax`;
			}
		},
		[closedSectionIds, cookieName],
	);

	const value = React.useMemo<AppSidebarSectionStateContextValue>(
		() => ({
			isSectionOpen: (sectionId) => !closedSectionIds.includes(sectionId),
			setSectionOpen,
		}),
		[closedSectionIds, setSectionOpen],
	);

	return (
		<AppSidebarSectionStateContext.Provider value={value}>
			{children}
		</AppSidebarSectionStateContext.Provider>
	);
}
AppSidebarSectionStateProvider.displayName = "AppSidebarSectionStateProvider";

// ---------------------------------------------------------------------------
// Shared rendering
// ---------------------------------------------------------------------------

function AppSidebarSectionLinkItem({
	link,
	className,
}: {
	link: AppSidebarSectionLink;
	className?: string;
}) {
	return (
		<SidebarMenuSubItem>
			<SidebarMenuSubButton
				asChild={link.asChild || !!link.href}
				isActive={link.isActive}
				className={className}
			>
				{link.children ? (
					link.children
				) : link.href ? (
					<a href={link.href}>{link.label}</a>
				) : (
					<span>{link.label}</span>
				)}
			</SidebarMenuSubButton>
		</SidebarMenuSubItem>
	);
}

const railIconButtonClassName =
	"flex h-12 w-12 mx-auto items-center justify-center rounded-md text-center transition-colors [&>svg]:size-5 [&>svg]:shrink-0 [&>svg]:text-white hover:bg-sidebar-accent hover:[&>svg]:text-white";

const railIconButtonActiveClassName = "bg-sidebar-accent [&>svg]:text-white";

// ---------------------------------------------------------------------------
// AppSidebarItem
// ---------------------------------------------------------------------------

function AppSidebarItem({
	label,
	isActive,
	href,
	asChild,
	children,
}: AppSidebarItemProps) {
	const { state, isMobile } = useSidebar();
	const isCollapsedRail = state === "collapsed" && !isMobile;

	const content = children ?? <a href={href}>{label}</a>;

	if (isCollapsedRail) {
		return (
			<SidebarMenuItem>
				<TooltipRoot delayDuration={TOOLTIP_INTENT_DELAY_MS}>
					<TooltipTrigger asChild>
						<SidebarMenuSubButton
							asChild={asChild || !!href}
							isActive={isActive}
							aria-label={label}
							className={cn(
								railIconButtonClassName,
								"px-0 py-0 translate-x-0 group-data-[collapsible=icon]:flex [&>*:not(svg)]:hidden",
								isActive && railIconButtonActiveClassName,
							)}
						>
							{content}
						</SidebarMenuSubButton>
					</TooltipTrigger>
					<TooltipContent side="right">{label}</TooltipContent>
				</TooltipRoot>
			</SidebarMenuItem>
		);
	}

	return (
		<SidebarMenuItem>
			<SidebarMenuSubButton
				asChild={asChild || !!href}
				isActive={isActive}
				className="h-8 translate-x-0 gap-2 px-3 [&>svg]:text-sidebar-muted-foreground [&[data-active=true]>svg]:text-sidebar-accent-foreground"
			>
				{content}
			</SidebarMenuSubButton>
		</SidebarMenuItem>
	);
}
AppSidebarItem.displayName = "AppSidebarItem";

// ---------------------------------------------------------------------------
// AppSidebarSection — collapsed rail icon with a hover flyout
// ---------------------------------------------------------------------------

function CollapsedSectionIcon({
	section,
	isSectionActive,
}: {
	section: AppSidebarSectionProps;
	isSectionActive: boolean;
}) {
	const [isHovering, setIsHovering] = React.useState(false);
	const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);

	const handleMouseEnter = () => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
		}
		hoverTimerRef.current = setTimeout(
			() => setIsHovering(true),
			FLYOUT_OPEN_DELAY_MS,
		);
	};

	const handleMouseLeave = () => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
		}
		hoverTimerRef.current = setTimeout(
			() => setIsHovering(false),
			FLYOUT_LEAVE_GRACE_MS,
		);
	};

	React.useEffect(
		() => () => {
			if (hoverTimerRef.current) {
				clearTimeout(hoverTimerRef.current);
				hoverTimerRef.current = null;
			}
		},
		[],
	);

	return (
		<Popover open={isHovering} onOpenChange={setIsHovering}>
			<PopoverTrigger asChild>
				<button
					type="button"
					aria-label={section.label}
					className={cn(
						railIconButtonClassName,
						isSectionActive && railIconButtonActiveClassName,
					)}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					{section.icon}
				</button>
			</PopoverTrigger>
			<PopoverContent
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				sideOffset={15}
				side="right"
				align="start"
				className="bg-sidebar border-sidebar-border text-sidebar-foreground rounded-xl duration-quick ease-smooth-out"
			>
				<h4
					className={cn(
						textVariants({ variant: "body-default" }),
						"text-sidebar-foreground mb-4 font-medium leading-relaxed",
					)}
				>
					{section.label}
				</h4>
				<ul className="space-y-1 list-none">
					{section.links.map((link) => (
						<AppSidebarSectionLinkItem key={link.id} link={link} />
					))}
					{section.extraContent}
				</ul>
			</PopoverContent>
		</Popover>
	);
}

// ---------------------------------------------------------------------------
// AppSidebarSection
// ---------------------------------------------------------------------------

/**
 * Icon-headed accordion. Every section is yours to open and close - the one that
 * owns the current page included - and each stays how you left it.
 *
 * Nothing reopens a section behind the reader: navigating into one they closed
 * leaves it closed, so the stored state is the only thing that decides. An
 * earlier revision locked the active section open, which meant the row you were
 * standing on could not be tidied away and the lock silently overrode the
 * reader's own choice on every navigation.
 */
/**
 * Whether the desktop sidebar has been collapsed at any point since the calling
 * component mounted. Fade expanded-only content in on this, not on every mount.
 *
 * The fade exists for expanding: it holds the labels back until the width
 * transition finishes, so they do not squash and wrap as the rail widens. On
 * the first render of a page there is no transition to hide, and fading then
 * only delays a menu the server already rendered. Reads no browser API, so the
 * server render and hydration agree.
 */
function useSidebarHasCollapsed(): boolean {
	const { state, isMobile } = useSidebar();
	const [hasCollapsed, setHasCollapsed] = React.useState(false);

	if (!hasCollapsed && state === "collapsed" && !isMobile) {
		setHasCollapsed(true);
	}

	return hasCollapsed;
}

function AppSidebarSection({
	label,
	icon,
	links,
	sectionId,
	defaultOpen = true,
	extraContent,
}: AppSidebarSectionProps) {
	const { state, isMobile } = useSidebar();
	const fadeIn = useSidebarHasCollapsed();
	const persistedState = React.useContext(AppSidebarSectionStateContext);
	const persistenceKey = sectionId ?? label;
	const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

	// Still read, but only by the collapsed rail, which marks the icon of the
	// section owning the page. It no longer has any say over the accordion.
	const isSectionActive = links.some((link) => link.isActive);
	const sectionOpen = persistedState
		? persistedState.isSectionOpen(persistenceKey)
		: uncontrolledOpen;

	const handleOpenChange = (next: boolean) => {
		if (persistedState) {
			persistedState.setSectionOpen(persistenceKey, next);
			return;
		}

		setUncontrolledOpen(next);
	};

	if (state === "collapsed" && !isMobile) {
		return (
			<SidebarMenuItem>
				<CollapsedSectionIcon
					section={{ label, icon, links, defaultOpen, extraContent }}
					isSectionActive={isSectionActive}
				/>
			</SidebarMenuItem>
		);
	}

	const header = (
		<>
			<span className="text-sidebar-muted-foreground flex shrink-0 [&>svg]:size-4 [&>svg]:shrink-0">
				{icon}
			</span>
			<span className="flex-1 truncate text-left">{label}</span>
			<ChevronRight
				size={16}
				className={cn(
					"shrink-0 opacity-55 transition-transform duration-fast ease-smooth-out",
					sectionOpen && "rotate-90",
				)}
			/>
		</>
	);

	const headerClassName = cn(
		textVariants({ variant: "body-default" }),
		"text-sidebar-foreground flex h-8 w-full items-center gap-2 rounded-md px-3 font-medium leading-relaxed",
		"cursor-pointer transition-colors hover:bg-sidebar-accent-hover/50 hover:text-sidebar-accent-foreground",
	);

	return (
		<Collapsible asChild open={sectionOpen} onOpenChange={handleOpenChange}>
			<SidebarMenuItem
				className={cn(fadeIn && "animate-sidebar-section-fade-in")}
			>
				<CollapsibleTrigger asChild>
					<button type="button" className={headerClassName}>
						{header}
					</button>
				</CollapsibleTrigger>
				<CollapsibleContent className="duration-fast ease-smooth-out">
					<SidebarMenuSub className="gap-1 py-1">
						{links.map((link) => (
							<AppSidebarSectionLinkItem
								key={link.id}
								link={link}
								className="h-8 pl-9 pr-2"
							/>
						))}
						{extraContent}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	);
}
AppSidebarSection.displayName = "AppSidebarSection";

// ---------------------------------------------------------------------------
// AppSidebarHeader
// ---------------------------------------------------------------------------

function AppSidebarHeader({ logo, className }: AppSidebarHeaderProps) {
	const { open, toggleSidebar, isMobile } = useSidebar();
	const fadeIn = useSidebarHasCollapsed();

	return (
		<div className={className}>
			<div
				className={cn(
					"flex items-center h-[60px] gap-2 px-3 py-4",
					open ? "justify-between" : "justify-center",
				)}
			>
				{(open || isMobile) && (
					<div className={cn("text-white", fadeIn && "animate-fade-in")}>
						{logo}
					</div>
				)}
				<button
					onClick={() => toggleSidebar()}
					className="hidden md:flex items-center justify-center w-6 h-7"
					aria-label="Toggle sidebar"
				>
					<ChevronLeft
						size={20}
						className={cn(
							"text-white transform duration-fast ease-smooth-out cursor-pointer",
							!open && "-rotate-180",
						)}
					/>
				</button>
			</div>
		</div>
	);
}
AppSidebarHeader.displayName = "AppSidebarHeader";

// ---------------------------------------------------------------------------
// AppSidebarFooter
// ---------------------------------------------------------------------------

const AppSidebarFooter = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<"div">
>(({ className, children, ...props }, ref) => (
	<SidebarFooterPrimitive ref={ref} className={className} {...props}>
		<div className="mx-auto w-full">{children}</div>
	</SidebarFooterPrimitive>
));
AppSidebarFooter.displayName = "AppSidebarFooter";

// ---------------------------------------------------------------------------
// AppSidebar
// ---------------------------------------------------------------------------

const AppSidebar = React.forwardRef<HTMLDivElement, AppSidebarProps>(
	({ header, footer, children, collapsible = "icon", ...props }, ref) => (
		<Sidebar ref={ref} collapsible={collapsible} {...props}>
			{header}
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu className="flex flex-col gap-1">
							{children}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
			{footer}
		</Sidebar>
	),
);
AppSidebar.displayName = "AppSidebar";

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
	AppSidebar,
	AppSidebarHeader,
	AppSidebarFooter,
	AppSidebarItem,
	AppSidebarSection,
	AppSidebarSectionStateProvider,
	useSidebarHasCollapsed,
};
export type {
	AppSidebarProps,
	AppSidebarHeaderProps,
	AppSidebarItemProps,
	AppSidebarSectionProps,
	AppSidebarSectionLink,
	AppSidebarSectionStateProviderProps,
};
